<template>
  <div class="call-recording-player">
    <p v-if="loading" class="call-recording-player__status">Loading recording…</p>
    <p v-else-if="error" class="call-recording-player__status call-recording-player__status--error">
      {{ error }}
    </p>
    <audio
      v-else-if="playbackUrl"
      ref="audioEl"
      controls
      preload="metadata"
      :src="playbackUrl"
      class="call-recording-player__audio"
      @error="onAudioError"
    >
      Your browser does not support audio playback.
    </audio>
    <p v-else class="call-recording-player__status">No recording is available.</p>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'
import {
  loadCallRecordingPlaybackUrl,
  revokeCallRecordingPlaybackUrl,
  resolveCallRecordingS3Key,
} from '@/utils/callRecording'

defineOptions({ name: 'CallRecordingPlayer' })

const props = defineProps({
  /** History / call row with recording_s3_key and/or recording_s3_url */
  source: { type: [Object, String], default: null },
})

const audioEl = ref(null)
const playbackUrl = ref(null)
const loading = ref(false)
const error = ref('')
let resolveToken = 0

function revokeCurrent() {
  revokeCallRecordingPlaybackUrl(playbackUrl.value)
  playbackUrl.value = null
}

function pausePlayback() {
  const el = audioEl.value
  if (!el) return
  try {
    el.pause()
    el.currentTime = 0
  } catch {
    /* ignore */
  }
}

async function loadPlayback() {
  const token = ++resolveToken
  loading.value = true
  error.value = ''
  pausePlayback()
  revokeCurrent()

  const s3Key =
    typeof props.source === 'string'
      ? String(props.source || '').trim()
      : resolveCallRecordingS3Key(props.source)

  if (!s3Key) {
    loading.value = false
    return
  }

  try {
    const resolved = await loadCallRecordingPlaybackUrl(
      typeof props.source === 'string' ? s3Key : props.source,
    )
    if (token !== resolveToken) {
      revokeCallRecordingPlaybackUrl(resolved)
      return
    }
    if (!resolved) {
      error.value = 'Unable to load this recording.'
      return
    }
    playbackUrl.value = resolved
  } catch (err) {
    if (token !== resolveToken) return
    console.error('Lead history recording failed', err)
    error.value = 'Unable to load this recording.'
  } finally {
    if (token === resolveToken) loading.value = false
  }
}

function onAudioError() {
  error.value = 'This recording could not be played.'
  pausePlayback()
  revokeCurrent()
}

watch(
  () => props.source,
  () => {
    void loadPlayback()
  },
  { immediate: true, deep: true },
)

onBeforeUnmount(() => {
  resolveToken += 1
  pausePlayback()
  revokeCurrent()
})
</script>

<style scoped>
.call-recording-player {
  min-width: 0;
}

.call-recording-player__audio {
  width: 100%;
  max-width: 100%;
  height: 36px;
}

.call-recording-player__status {
  margin: 0;
  font-size: 0.78rem;
  color: var(--y-text-muted, #64748b);
}

.call-recording-player__status--error {
  color: #dc2626;
}
</style>
